import { useEffect, useState } from "react";
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";
import { Button } from "../shadcn/button";
import { Label } from "../shadcn/label";
import { Skeleton } from "../shadcn/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 40, 50];

/**
 * Table footer pagination (shadcn/ui, modelled on Pitstop's TableFooter).
 *
 * Required props (unchanged from the old react-paginate version, so existing
 * call sites keep working):
 *  - lengthofItems: total number of records
 *  - limit:         records per page
 *  - currentPage:   0-indexed current page
 *  - onPageChange:  called with `{ selected: <0-indexed page> }`
 *
 * Optional:
 *  - onLimitChange: shows a "Rows per page" select; called with the new limit
 *  - isLoading / isFetching: show skeletons / disable controls while loading
 */
function Pagination({
  lengthofItems,
  onPageChange,
  limit,
  currentPage,
  onLimitChange,
  isLoading = false,
  isFetching = false,
}) {
  const totalCount = Number(lengthofItems) || 0;
  const perPage = Number(limit) || 0;
  const pageCount = perPage > 0 ? Math.ceil(totalCount / perPage) : 0;
  const pageNo = Math.min(
    Math.max(Number(currentPage) || 0, 0),
    Math.max(pageCount - 1, 0)
  );

  const [goToValue, setGoToValue] = useState(String(pageNo + 1));

  // Keep the "Go To" input in sync when the page changes from elsewhere.
  useEffect(() => {
    setGoToValue(String(pageNo + 1));
  }, [pageNo]);

  const showSkeletons = isLoading || (isFetching && totalCount === 0);
  const controlsDisabled = isLoading || isFetching;

  if (pageCount < 1 && !showSkeletons) return null;

  const isFirst = pageNo === 0;
  const isLast = pageNo >= pageCount - 1;
  const from = pageNo * perPage + 1;
  const to = Math.min((pageNo + 1) * perPage, totalCount);

  const goTo = (page) => onPageChange({ selected: page });

  const handleGoToSubmit = (e) => {
    e.preventDefault();
    const target = parseInt(goToValue, 10);
    if (Number.isNaN(target)) {
      setGoToValue(String(pageNo + 1));
      return;
    }
    const clamped = Math.min(Math.max(target, 1), pageCount);
    setGoToValue(String(clamped));
    if (clamped - 1 !== pageNo) goTo(clamped - 1);
  };

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-col items-center justify-between gap-4 sm:flex-row"
    >
      {/* Showing X to Y of Z */}
      <div className="text-sm text-muted-foreground">
        {showSkeletons ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <p className="font-medium">
            Showing <span className="text-foreground">{from}</span> to{" "}
            <span className="text-foreground">{to}</span> of{" "}
            <span className="text-foreground">{totalCount}</span> row(s)
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {/* Rows per page (opt-in) */}
        {onLimitChange && (
          <div className="flex items-center gap-2">
            {showSkeletons ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <Label
                  htmlFor="pagination-rows-per-page"
                  className="hidden text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:inline-block"
                >
                  Rows per page
                </Label>
                <Select
                  value={`${perPage}`}
                  onValueChange={(value) => onLimitChange(Number(value))}
                  disabled={controlsDisabled}
                >
                  <SelectTrigger
                    id="pagination-rows-per-page"
                    size="sm"
                    className="w-[70px] bg-background"
                  >
                    <SelectValue placeholder={perPage} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[...new Set([...ROWS_PER_PAGE_OPTIONS, perPage])]
                      .sort((a, b) => a - b)
                      .map((size) => (
                        <SelectItem key={size} value={`${size}`}>
                          {size}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        )}

        {/* Page X of Y */}
        <div className="flex min-w-[80px] items-center justify-center text-sm font-medium">
          {showSkeletons ? (
            <Skeleton className="h-4 w-16" />
          ) : (
            <span className="whitespace-nowrap text-muted-foreground">
              Page{" "}
              <span className="font-semibold text-foreground">{pageNo + 1}</span>{" "}
              of{" "}
              <span className="font-semibold text-foreground">{pageCount}</span>
            </span>
          )}
        </div>

        {/* First / prev / next / last */}
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="First page"
            className="hidden h-8 w-8 sm:inline-flex"
            onClick={() => goTo(0)}
            disabled={isFirst || controlsDisabled}
          >
            <LuChevronsLeft />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Previous page"
            className="h-8 w-8"
            onClick={() => goTo(pageNo - 1)}
            disabled={isFirst || controlsDisabled}
          >
            <LuChevronLeft />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Next page"
            className="h-8 w-8"
            onClick={() => goTo(pageNo + 1)}
            disabled={isLast || controlsDisabled}
          >
            <LuChevronRight />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Last page"
            className="hidden h-8 w-8 sm:inline-flex"
            onClick={() => goTo(pageCount - 1)}
            disabled={isLast || controlsDisabled}
          >
            <LuChevronsRight />
          </Button>
        </div>

        {/* Go To */}
        <form onSubmit={handleGoToSubmit} className="flex items-center gap-2">
          <Label htmlFor="pagination-go-to" className="text-muted-foreground">
            Go To:
          </Label>
          <input
            id="pagination-go-to"
            type="number"
            min={1}
            max={pageCount || 1}
            value={goToValue}
            onChange={(e) => setGoToValue(e.target.value)}
            disabled={controlsDisabled}
            className="h-8 w-16 rounded-md border border-input bg-background px-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <Button type="submit" size="sm" disabled={controlsDisabled}>
            Go
          </Button>
        </form>
      </div>
    </nav>
  );
}

export default Pagination;
